import React, { useEffect, useRef, useState, useCallback } from 'react'
import axios from 'axios'
import { useNavigate } from 'react-router-dom'
import { Bell, CheckCheck, Settings, Inbox as InboxIcon } from 'lucide-react'

// NotificationBell — a bell button with an unread badge that polls
// GET /notifications/unread-count every ~60s. Clicking opens a dropdown of
// recent notifications (GET /notifications): severity dot, title, body,
// relative time, optional link. Items mark read on click (POST /:id/read),
// "Mark all read" hits POST /read-all. Nothing here knows the tenant — the
// global axios interceptor attaches the Bearer token.
//
// VISUAL LAYER ONLY: the flat "Nunito" aesthetic — soft palette, rounded
// cards, light+dark theme tokens — mirrors Connections/GrowthLab and is scoped
// under `.nb-root` (styles prefixed `.nb-*`) so nothing leaks out.

const API = '/api/v1'

const NB_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;500;600;700;800&display=swap');
.nb-root{
  --nb-panel:#FFFFFF; --nb-panel2:#F6F6F0; --nb-line:#ECEBE3;
  --nb-ink:#2E3A45; --nb-mut:#7C8794; --nb-dim:#AEB6BD; --nb-accent:#4E9AC4;
  --nb-hover:#F6F6F0; --nb-unread:#F0F7FB;
  --nb-info:#4E9AC4; --nb-success:#4CAF7D; --nb-warn:#E0A93B; --nb-danger:#DB5C5C;
  --nb-badge:#DB5C5C;
  --nb-shadow:0 1px 2px rgba(46,58,69,.05),0 12px 32px rgba(46,58,69,.12);
  font-family:Nunito,system-ui,-apple-system,Segoe UI,sans-serif;
  color:var(--nb-ink); -webkit-font-smoothing:antialiased; position:relative; display:inline-block;
}
[data-theme="dark"] .nb-root{
  --nb-panel:#171B22; --nb-panel2:#12161C; --nb-line:#262C36;
  --nb-ink:#E7ECF1; --nb-mut:#95A0AD; --nb-dim:#5C6672; --nb-accent:#5FB0DB;
  --nb-hover:#1E242D; --nb-unread:#132029;
  --nb-info:#5FB0DB; --nb-success:#5FC493; --nb-warn:#E8BB5A; --nb-danger:#E87070;
  --nb-badge:#E05252;
  --nb-shadow:0 1px 2px rgba(0,0,0,.3),0 14px 34px rgba(0,0,0,.5);
}
@media (prefers-color-scheme:dark){:root:not([data-theme="light"]) .nb-root{
  --nb-panel:#171B22; --nb-panel2:#12161C; --nb-line:#262C36;
  --nb-ink:#E7ECF1; --nb-mut:#95A0AD; --nb-dim:#5C6672; --nb-accent:#5FB0DB;
  --nb-hover:#1E242D; --nb-unread:#132029;
  --nb-info:#5FB0DB; --nb-success:#5FC493; --nb-warn:#E8BB5A; --nb-danger:#E87070;
  --nb-badge:#E05252;
  --nb-shadow:0 1px 2px rgba(0,0,0,.3),0 14px 34px rgba(0,0,0,.5);
}}
.nb-root *{box-sizing:border-box;}
.nb-btn{position:relative;display:inline-flex;align-items:center;justify-content:center;width:38px;height:38px;border-radius:11px;border:1px solid var(--nb-line);background:var(--nb-panel);color:var(--nb-mut);cursor:pointer;transition:.16s;}
.nb-btn:hover{color:var(--nb-ink);background:var(--nb-hover);}
.nb-btn.open{color:var(--nb-ink);border-color:var(--nb-accent);}
.nb-badge{position:absolute;top:-5px;right:-5px;min-width:18px;height:18px;padding:0 5px;border-radius:9px;background:var(--nb-badge);color:#fff;font-size:10px;font-weight:800;line-height:18px;text-align:center;border:2px solid var(--nb-panel);}
.nb-scrim{position:fixed;inset:0;z-index:40;}
.nb-pop{position:absolute;top:calc(100% + 8px);right:0;z-index:50;width:380px;max-width:88vw;background:var(--nb-panel);border:1px solid var(--nb-line);border-radius:16px;box-shadow:var(--nb-shadow);overflow:hidden;animation:nb-in .16s ease both;}
@keyframes nb-in{from{opacity:0;transform:translateY(-6px);}to{opacity:1;transform:none;}}
.nb-head{display:flex;align-items:center;justify-content:space-between;padding:14px 16px;border-bottom:1px solid var(--nb-line);}
.nb-head h3{margin:0;font-size:15px;font-weight:800;letter-spacing:-.01em;}
.nb-head .nb-actions{display:flex;align-items:center;gap:6px;}
.nb-link{display:inline-flex;align-items:center;gap:5px;font-size:12px;font-weight:700;color:var(--nb-accent);background:none;border:none;cursor:pointer;padding:5px 8px;border-radius:8px;transition:.16s;font-family:inherit;}
.nb-link:hover{background:var(--nb-hover);}
.nb-link:disabled{color:var(--nb-dim);cursor:default;background:none;}
.nb-list{max-height:min(60vh,440px);overflow-y:auto;}
.nb-item{display:flex;gap:11px;padding:13px 16px;border-bottom:1px solid var(--nb-line);cursor:pointer;transition:.14s;background:none;border-left:none;border-right:none;border-top:none;width:100%;text-align:left;font-family:inherit;}
.nb-item:hover{background:var(--nb-hover);}
.nb-item.unread{background:var(--nb-unread);}
.nb-item:last-child{border-bottom:none;}
.nb-dot{flex:0 0 auto;width:9px;height:9px;border-radius:50%;margin-top:5px;background:var(--nb-info);}
.nb-dot.info{background:var(--nb-info);} .nb-dot.success{background:var(--nb-success);}
.nb-dot.warn{background:var(--nb-warn);} .nb-dot.warning{background:var(--nb-warn);}
.nb-dot.danger{background:var(--nb-danger);} .nb-dot.error{background:var(--nb-danger);} .nb-dot.critical{background:var(--nb-danger);}
.nb-body{min-width:0;flex:1;}
.nb-t{font-size:13px;font-weight:800;color:var(--nb-ink);line-height:1.35;}
.nb-b{font-size:12.5px;font-weight:500;color:var(--nb-mut);line-height:1.45;margin-top:2px;word-break:break-word;}
.nb-time{font-size:11px;font-weight:700;color:var(--nb-dim);margin-top:4px;}
.nb-empty{display:flex;flex-direction:column;align-items:center;gap:8px;padding:40px 20px;color:var(--nb-dim);text-align:center;}
.nb-empty p{margin:0;font-size:13px;font-weight:600;}
.nb-foot{padding:10px 16px;border-top:1px solid var(--nb-line);background:var(--nb-panel2);}
.nb-foot button{width:100%;display:inline-flex;align-items:center;justify-content:center;gap:6px;font-size:12.5px;font-weight:800;color:var(--nb-mut);background:none;border:none;cursor:pointer;padding:6px;border-radius:9px;transition:.16s;font-family:inherit;}
.nb-foot button:hover{color:var(--nb-ink);background:var(--nb-hover);}
`

let _nbStyleInjected = false
function NBStyle() {
  useEffect(() => {
    if (_nbStyleInjected) return
    const el = document.createElement('style')
    el.setAttribute('data-nb-style', '1')
    el.textContent = NB_CSS
    document.head.appendChild(el)
    _nbStyleInjected = true
  }, [])
  return null
}

function relTime(value) {
  if (!value) return ''
  const then = new Date(value).getTime()
  if (Number.isNaN(then)) return ''
  const s = Math.max(0, Math.floor((Date.now() - then) / 1000))
  if (s < 45) return 'just now'
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.floor(h / 24)
  if (d < 7) return `${d}d ago`
  const w = Math.floor(d / 7)
  if (w < 5) return `${w}w ago`
  return new Date(then).toLocaleDateString()
}

export default function NotificationBell() {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [count, setCount] = useState(0)
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const mounted = useRef(true)

  useEffect(() => () => { mounted.current = false }, [])

  const fetchCount = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/notifications/unread-count`)
      const c = r.data?.count ?? r.data?.unread ?? r.data?.unreadCount ?? 0
      if (mounted.current) setCount(Number(c) || 0)
    } catch { /* offline / not provisioned — stay quiet */ }
  }, [])

  const fetchList = useCallback(async () => {
    setLoading(true)
    try {
      const r = await axios.get(`${API}/notifications`, { params: { limit: 20 } })
      const list = Array.isArray(r.data) ? r.data : (r.data?.notifications || r.data?.items || [])
      if (mounted.current) setItems(list)
    } catch {
      if (mounted.current) setItems([])
    } finally {
      if (mounted.current) setLoading(false)
    }
  }, [])

  // Poll the unread count every ~60s (and once on mount).
  useEffect(() => {
    fetchCount()
    const id = setInterval(fetchCount, 60000)
    return () => clearInterval(id)
  }, [fetchCount])

  const toggle = () => {
    const next = !open
    setOpen(next)
    if (next) fetchList()
  }

  const isRead = (n) => n.read ?? n.isRead ?? n.read_at != null ?? false
  const idOf = (n) => n.id ?? n._id ?? n.notificationId
  const linkOf = (n) => n.link || n.href || n.url || n.path
  const timeOf = (n) => n.createdAt || n.created_at || n.timestamp || n.time
  const sevOf = (n) => String(n.severity || n.level || n.type || 'info').toLowerCase()

  const markRead = async (n) => {
    const id = idOf(n)
    if (!id || isRead(n)) return
    setItems(prev => prev.map(x => idOf(x) === id ? { ...x, read: true, isRead: true } : x))
    setCount(c => Math.max(0, c - 1))
    try { await axios.post(`${API}/notifications/${id}/read`) } catch { /* best-effort */ }
  }

  const onItem = async (n) => {
    await markRead(n)
    const link = linkOf(n)
    if (link) {
      setOpen(false)
      if (/^https?:\/\//i.test(link)) window.open(link, '_blank', 'noopener')
      else navigate(link)
    }
  }

  const markAll = async () => {
    setBusy(true)
    setItems(prev => prev.map(x => ({ ...x, read: true, isRead: true })))
    setCount(0)
    try { await axios.post(`${API}/notifications/read-all`) } catch { /* best-effort */ }
    finally { if (mounted.current) setBusy(false) }
  }

  const hasUnread = items.some(n => !isRead(n))

  return (
    <div className="nb-root">
      <NBStyle />
      <button
        className={`nb-btn${open ? ' open' : ''}`}
        onClick={toggle}
        aria-label={`Notifications${count ? ` (${count} unread)` : ''}`}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <Bell size={18} />
        {count > 0 && <span className="nb-badge">{count > 99 ? '99+' : count}</span>}
      </button>

      {open && (
        <>
          <div className="nb-scrim" onClick={() => setOpen(false)} />
          <div className="nb-pop" role="dialog" aria-label="Notifications">
            <div className="nb-head">
              <h3>Notifications</h3>
              <div className="nb-actions">
                <button className="nb-link" onClick={markAll} disabled={busy || !hasUnread}>
                  <CheckCheck size={13} /> Mark all read
                </button>
              </div>
            </div>

            <div className="nb-list">
              {loading && items.length === 0 && (
                <div className="nb-empty"><p>Loading…</p></div>
              )}
              {!loading && items.length === 0 && (
                <div className="nb-empty">
                  <InboxIcon size={26} />
                  <p>You're all caught up.</p>
                </div>
              )}
              {items.map((n, i) => {
                const read = isRead(n)
                const sev = sevOf(n)
                return (
                  <button
                    key={idOf(n) ?? i}
                    className={`nb-item${read ? '' : ' unread'}`}
                    onClick={() => onItem(n)}
                  >
                    <span className={`nb-dot ${sev}`} />
                    <span className="nb-body">
                      <span className="nb-t">{n.title || n.subject || 'Notification'}</span>
                      {(n.body || n.message || n.text) && (
                        <span className="nb-b">{n.body || n.message || n.text}</span>
                      )}
                      <span className="nb-time">{relTime(timeOf(n))}</span>
                    </span>
                  </button>
                )
              })}
            </div>

            <div className="nb-foot">
              <button onClick={() => { setOpen(false); navigate('/notifications') }}>
                <Settings size={13} /> Notification settings
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
